/**
 * In-memory UserStore helpers for denial/query status-update reasons.
 *
 * Upserts by the composite key `order_id` × `j_code_id`, overwriting an
 * existing entry when both match; otherwise appends a new entry.
 *
 * @module denialQueryReasonUserStoreHelper
 */

import { createLogger } from "@risalabs_frontend_org/logger";
import { UserGeneralInfoContextKeys } from "../../../../enums/userGeneralInfoContextKeys";
import { UserStore } from "../../../../store/UserGeneralInfoStore";

const log = createLogger(
  "DenialQueryReasonStore",
  "denialQueryReasonUserStoreHelper.ts",
);

/** Shape of a denial or query reason staged in UserStore. */
export interface DenialQueryReasonStoreEntry {
  /** Medical PA order ID. */
  order_id: string;
  /** J-code row identifier associated with the status change. */
  j_code_id: string;
  /** Selected status-change reason. */
  reason: string;
  /** Bucket type for the reason. */
  type: "denial" | "query";
}

const STORE_KEY = UserGeneralInfoContextKeys.DENIAL_QUERY_REASONS;

/**
 * Reads the current denial/query reason array from UserStore.
 *
 * @returns The stored entries array, or an empty array when none exist.
 */
export const getDenialQueryReasonsFromStore =
  (): DenialQueryReasonStoreEntry[] => {
    const entries = UserStore.getItem(STORE_KEY);
    return Array.isArray(entries) ? entries : [];
  };

/**
 * Upserts a denial/query reason into UserStore.
 *
 * If an entry already exists for the same `order_id` and `j_code_id`, it is
 * overwritten; otherwise a new entry is appended.
 *
 * @param entry - Reason payload to insert or overwrite.
 * @returns The updated store array after the upsert.
 */
export const upsertDenialQueryReasonInStore = (
  entry: DenialQueryReasonStoreEntry,
): DenialQueryReasonStoreEntry[] => {
  const existing = getDenialQueryReasonsFromStore();
  const matchIndex = existing.findIndex(
    (item) =>
      item.order_id === entry.order_id && item.j_code_id === entry.j_code_id,
  );

  if (matchIndex !== -1) {
    existing[matchIndex] = entry;
    log.info("Overwrote existing entry", entry);
  } else {
    existing.push(entry);
    log.info("Inserted new entry", entry);
  }

  UserStore.setItem(STORE_KEY, existing);
  log.debug("Store after upsert", existing);
  return existing;
};

/**
 * Clears all denial/query reason entries from UserStore.
 */
export const clearDenialQueryReasonsFromStore = (): void => {
  const previous = getDenialQueryReasonsFromStore();
  UserStore.removeItem(STORE_KEY);
  log.warn("Cleared all entries", previous);
};

/**
 * Removes denial/query reason entries for a specific order from UserStore.
 *
 * Leaves entries for other orders intact. Removes the store key entirely when
 * no entries remain.
 *
 * @param orderId - Medical PA order ID whose staged reasons should be removed.
 */
export const clearDenialQueryReasonsFromStoreByOrderId = (
  orderId: string,
): void => {
  const existing = getDenialQueryReasonsFromStore();
  const remaining = existing.filter((entry) => entry.order_id !== orderId);
  const removed = existing.filter((entry) => entry.order_id === orderId);

  if (remaining.length === 0) {
    UserStore.removeItem(STORE_KEY);
  } else {
    UserStore.setItem(STORE_KEY, remaining);
  }

  log.warn("Cleared entries for order", { orderId, removed });
};
